import React from 'react';
import {
  Target,
  Rocket,
  Users,
  Wrench,
  GraduationCap,
  Briefcase,
  Star,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface AboutSectionProps {
  className?: string;
}

const AboutSection: React.FC<AboutSectionProps> = ({ className }) => {
  const highlights = [
    {
      icon: Target,
      title: 'Results-Driven',
      description:
        'Proven track record of delivering robust software solutions that drive business value and enhance user experience.',
    },
    {
      icon: Rocket,
      title: 'Process Optimization',
      description:
        'Spearheading initiatives to optimize software development processes and technical stack for maximum team effectiveness.',
    },
    {
      icon: Users,
      title: 'Team Leadership',
      description:
        'Expertise in mentoring engineers and contributing as a valuable member of dynamic software engineering teams.',
    },
    {
      icon: Wrench,
      title: 'Full-Stack Expertise',
      description:
        'Comprehensive experience across the entire software development lifecycle, from frontend to backend systems.',
    },
  ];

  const coreValues = [
    'Innovation',
    'Quality',
    'Collaboration',
    'Continuous Learning',
    'Technical Excellence',
    'Mentorship',
  ];

  const achievements = [
    {
      metric: '9+',
      label: 'Years Experience',
      description: 'Full-stack development',
    },
    {
      metric: 'M.S.',
      label: 'Software Engineering',
      description: 'Advanced degree',
    },
    {
      metric: '50+',
      label: 'Projects Delivered',
      description: 'Robust solutions',
    },
    {
      metric: '15+',
      label: 'Engineers Mentored',
      description: 'Team development',
    },
  ];

  return (
    <section id='about' className={cn('py-20 bg-muted/30', className)}>
      <div className='container mx-auto px-4'>
        {/* Section Header */}
        <div className='text-center mb-16'>
          <h2 className='text-3xl sm:text-4xl font-bold text-foreground mb-4'>
            About Me
          </h2>
          <p className='text-lg text-muted-foreground max-w-3xl mx-auto'>
            Passionate about building scalable solutions and leading
            high-performing teams
          </p>
        </div>

        {/* Main Content Grid */}
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16'>
          {/* Professional Summary */}
          <div className='space-y-6'>
            <div>
              <h3 className='text-2xl font-semibold text-foreground mb-4'>
                Professional Summary
              </h3>
              <div className='space-y-4 text-muted-foreground'>
                <p>
                  I'm a results-driven full-stack software engineer with a
                  Master's degree in Software Engineering and over 9 years of
                  experience developing robust software solutions that make a
                  real impact.
                </p>
                <p>
                  My passion lies in enhancing team effectiveness by
                  spearheading initiatives to optimize software development
                  processes and technical stacks. I believe that great software
                  is built by great teams, and I'm committed to fostering
                  environments where both code and people can thrive.
                </p>
                <p>
                  I'm actively seeking opportunities to leverage my expertise in
                  mentoring engineers while contributing as a valuable member of
                  a dynamic software engineering team that values innovation,
                  quality, and continuous growth.
                </p>
              </div>
            </div>

            {/* Core Values */}
            <div>
              <h4 className='text-lg font-semibold text-foreground mb-3'>
                Core Values
              </h4>
              <div className='flex flex-wrap gap-2'>
                {coreValues.map(value => (
                  <Badge key={value} variant='secondary' className='text-sm'>
                    {value}
                  </Badge>
                ))}
              </div>
            </div>
          </div>

          {/* Achievements Grid */}
          <div className='grid grid-cols-2 gap-4'>
            {achievements.map((achievement, index) => (
              //i want the card content to be centered
              <Card
                key={index}
                className='text-center flex items-center justify-center'
              >
                <CardContent className='pt-6'>
                  <div className='text-2xl font-bold text-primary mb-1'>
                    {achievement.metric}
                  </div>
                  <div className='text-sm font-medium text-foreground mb-1'>
                    {achievement.label}
                  </div>
                  <div className='text-xs text-muted-foreground'>
                    {achievement.description}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Key Highlights */}
        <div>
          <h3 className='text-2xl font-semibold text-foreground text-center mb-8'>
            What I Bring to the Table
          </h3>
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6'>
            {highlights.map((highlight, index) => {
              const IconComponent = highlight.icon;
              return (
                <Card
                  key={index}
                  className='text-center hover:shadow-lg transition-shadow'
                >
                  <CardHeader className='pb-4'>
                    <div className='mb-2 flex justify-center'>
                      <IconComponent className='w-8 h-8 text-primary' />
                    </div>
                    <CardTitle className='text-lg'>{highlight.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className='text-sm text-muted-foreground'>
                      {highlight.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Call to Action */}
        <div className='text-center mt-16'>
          <p className='text-lg text-muted-foreground mb-4'>
            Ready to build something amazing together?
          </p>
          <div className='flex flex-col sm:flex-row gap-4 justify-center'>
            <Badge
              variant='outline'
              className='text-sm px-4 py-2 flex items-center gap-2'
            >
              <GraduationCap className='w-4 h-4' />
              M.S. Software Engineering
            </Badge>
            <Badge
              variant='outline'
              className='text-sm px-4 py-2 flex items-center gap-2'
            >
              <Briefcase className='w-4 h-4' />
              9+ Years Experience
            </Badge>
            <Badge
              variant='outline'
              className='text-sm px-4 py-2 flex items-center gap-2'
            >
              <Star className='w-4 h-4' />
              Full-Stack Expertise
            </Badge>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
